import { HttpContextToken, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import * as Pako from 'pako';
import { defer, Subject, throwError } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ModalService } from '../../shared/services/modal/modal.service';
import { SpinnerService } from '../services/spinner/spinner.service';
import { userStore } from '../user.store';

export const BYPASS_LOG = new HttpContextToken(() => false);

let pendingRequests = 0;
const cancelAllRequests$ = new Subject<void>();

export const loadingInterceptor: HttpInterceptorFn = (request, next) => {
  const store = inject(userStore);
  const spinnerService = inject(SpinnerService);
  const SSO = localStorage.getItem('SSO') === 'true';
  const getTokenMail = localStorage.getItem('getTokenMail');
  const modalService = inject(ModalService);
  if (!request.url.includes('cdn.paddock-one.com')) {
    request = request.clone({ withCredentials: true, headers: request.headers.set('sso', SSO ? 'true' : 'false') });
  }
  if (getTokenMail) {
    request = request.clone({ headers: request.headers.set('get-token-mail', getTokenMail) });
  }

  const apiBase = environment.apiUrl.replace(/\/$/, '');
  const path = request.url.slice(apiBase.length).split('?')[0];
  const publicAuthRequest = path.startsWith('/auth/') && !['/auth/profile', '/auth/edit-profile', '/auth/password-verify'].includes(path);
  const needsRefresh = SSO && request.url.startsWith(`${apiBase}/`) && !publicAuthRequest && path !== '/common/refresh-token-expiration' && path !== '/common/ping' && path !== '/auth/logout';
  const sendRequest = () => (needsRefresh ? defer(() => store.refreshAppwriteJwt()).pipe(switchMap(() => next(request))) : next(request));

  if (request.context.get(BYPASS_LOG)) {
    return sendRequest();
  }

  pendingRequests++;
  const startTimeout = setTimeout(() => spinnerService.loading.set(true), 250);
  const timeoutId = setTimeout(() => spinnerService.isSlowConnection.set(true), 2000);

  if (request.body) {
    try {
      const encoded = new TextEncoder().encode(JSON.stringify(request.body));
      const compressedBody = Pako.gzip(encoded);
      request = request.clone({
        headers: request.headers.set('Content-Type', 'application/json').set('Content-Encoding', 'gzip'),
        body: new Blob([compressedBody], { type: 'application/json' }),
      });
    } catch (error) {
      console.error('Error compressing request body:', error);
    }
  }

  return sendRequest().pipe(
    takeUntil(cancelAllRequests$),
    catchError((error) => {
      if (error.status === 418) {
        cancelAllRequests$.next();
        modalService.openAlert('warning', 'Session expirée', 'Votre session a expiré. Veuillez vous reconnecter.', () => {
          const lastUrl = sessionStorage.getItem('lastUrl');
          localStorage.clear();
          sessionStorage.clear();
          sessionStorage.setItem('redirectUrl', lastUrl ?? '/');

          store.logout();
          modalService.closeAlert();
        });
        return throwError(() => error);
      }
      if (error.status === 401 && store.isAuthentified()) {
        localStorage.clear();
        sessionStorage.clear();
        store.logout();
      }
      return throwError(() => error);
    }),
    finalize(() => {
      clearTimeout(timeoutId);
      clearTimeout(startTimeout);
      pendingRequests--;
      if (pendingRequests === 0) {
        spinnerService.loading.set(false);
        spinnerService.isSlowConnection.set(false);
      }
    }),
  );
};
